// src/components/admin/blocks-editor/BlocksEditor.tsx
//
// Éditeur visuel des blocs d'une étape de parcours.
// - Chaque bloc est rendu comme une card avec un formulaire structuré
//   spécifique à son type (au lieu d'éditer du JSON à la main)
// - Boutons par bloc : monter / descendre / supprimer / replier
// - Bouton "+ Ajouter un bloc" en bas avec dropdown des 9 types
// - Pour les types peu utilisés (table, team-org, video, pdf), l'édition
//   reste en JSON inline (textarea), avec un message qui invite à basculer
//   en mode JSON avancé pour plus de confort.

import { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  X,
  Plus,
  Trash2,
  GripVertical,
  FileText,
  Image as ImageIcon,
  Video,
  BarChart3,
  Clock,
  Users,
  Table as TableIcon,
  Code,
  FileDown,
  AlertCircle,
  ChevronsDown,
  ChevronsUp,
  Send,
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface AnyBlock {
  type: string;
  [key: string]: unknown;
}

interface Props {
  blocks: AnyBlock[];
  onChange: (blocks: AnyBlock[]) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

interface TypeMeta {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  advanced?: boolean;
}

const TYPE_META: Record<string, TypeMeta> = {
  'rich-text': {
    label: 'Texte riche',
    description: 'Paragraphes, titres, listes, liens. Idéal pour intro / contexte.',
    icon: FileText,
  },
  'image': {
    label: 'Image',
    description: 'Photo, illustration, capture. Avec légende et alignement.',
    icon: ImageIcon,
  },
  'video': {
    label: 'Vidéo',
    description: 'Coller un lien YouTube / Vimeo, lecteur intégré avec play overlay.',
    icon: Video,
  },
  'stats-grid': {
    label: 'Statistiques',
    description: 'Grille de chiffres clés (idéal 4 items). Valeur + label + description.',
    icon: BarChart3,
  },
  'timeline': {
    label: 'Timeline',
    description: 'Frise chronologique : date / titre / description par événement.',
    icon: Clock,
  },
  'embed-iframe': {
    label: 'Présentation / formulaire',
    description: 'Prezi, Genially, Canva, Calendly, Google Maps, HubSpot Form…',
    icon: Code,
  },
  'pdf': {
    label: 'PDF',
    description: 'Liseuse intégrée + bouton télécharger. URL du fichier.',
    icon: FileDown,
  },
  'team-org': {
    label: 'Équipe',
    description: 'Organigramme par niveaux (direction / management / équipe).',
    icon: Users,
    advanced: true,
  },
  'table': {
    label: 'Tableau',
    description: 'Tableau de données (colonnes + lignes).',
    icon: TableIcon,
    advanced: true,
  },
  'a-vous-de-jouer': {
    label: 'À vous de jouer',
    description:
      'Formulaire final (parcours découverte) : le candidat dit s\'il veut rencontrer l\'équipe → emails client + recruteur. À placer en dernière étape.',
    icon: Send,
  },
};

const TEMPLATES: Record<string, AnyBlock> = {
  'rich-text': { type: 'rich-text', html: '<p>Votre contenu ici.</p>' },
  'image': { type: 'image', url: '', alt: '', caption: '', align: 'center' },
  'video': { type: 'video', url: '', title: '' },
  'stats-grid': {
    type: 'stats-grid',
    items: [
      { value: '', label: '', description: '' },
      { value: '', label: '', description: '' },
    ],
  },
  'timeline': {
    type: 'timeline',
    items: [{ year: '', headline: '', description: '' }],
  },
  'embed-iframe': { type: 'embed-iframe', src: '', title: 'Formulaire', height: 600 },
  'table': {
    type: 'table',
    columns: ['Colonne 1', 'Colonne 2'],
    rows: [['Valeur', 'Valeur']],
  },
  'team-org': {
    type: 'team-org',
    title: '',
    description: '',
    levels: [{ title: 'Direction', members: [{ name: '', role: '', photoUrl: '', linkedinUrl: '' }] }],
  },
  'pdf': { type: 'pdf', url: '', title: 'Document' },
  'a-vous-de-jouer': { type: 'a-vous-de-jouer' },
};

// Normalise le type (alias stats → stats-grid)
function normType(t: string): string {
  if (t === 'stats' || t === 'stats_grid') return 'stats-grid';
  if (t === 'team_org') return 'team-org';
  return t;
}

const inputClass =
  'w-full rounded-lg ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-white px-3 py-2 text-sm text-[#30475e] outline-none transition-all';
const labelClass =
  'block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1';

// ── Composant principal ──────────────────────────────────────────────────────

export default function BlocksEditor({ blocks, onChange }: Props) {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [addOpen, setAddOpen] = useState(false);

  function update(idx: number, patch: Partial<AnyBlock>) {
    const next = blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    onChange(next);
  }

  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  }

  function remove(idx: number) {
    if (!confirm('Supprimer ce bloc ?')) return;
    onChange(blocks.filter((_, i) => i !== idx));
  }

  function add(type: string) {
    const tpl = TEMPLATES[type];
    if (!tpl) return;
    onChange([...blocks, JSON.parse(JSON.stringify(tpl))]);
    setAddOpen(false);
    // Ouvre le nouveau bloc
    setCollapsed((prev) => ({ ...prev, [blocks.length]: false }));
  }

  function expandAll() {
    setCollapsed({});
  }
  function collapseAll() {
    const all: Record<number, boolean> = {};
    blocks.forEach((_, i) => (all[i] = true));
    setCollapsed(all);
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-neutral-500">
          {blocks.length} bloc{blocks.length !== 1 ? 's' : ''} dans cette étape
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={expandAll}
            className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-[#30475e] px-2 py-1 rounded transition-colors"
            title="Tout déplier"
          >
            <ChevronsDown className="w-3 h-3" />
            Déplier
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-[#30475e] px-2 py-1 rounded transition-colors"
            title="Tout replier"
          >
            <ChevronsUp className="w-3 h-3" />
            Replier
          </button>
        </div>
      </div>

      {/* Liste des blocs */}
      {blocks.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-neutral-200 bg-neutral-50 px-5 py-10 text-center">
          <p className="text-sm text-neutral-500 mb-3">Aucun bloc dans cette étape.</p>
          <p className="text-xs text-neutral-400">
            Cliquez sur « Ajouter un bloc » ci-dessous pour commencer.
          </p>
        </div>
      )}

      {blocks.map((block, idx) => {
        const type = normType(block.type);
        const meta = TYPE_META[type];
        const Icon = meta?.icon ?? AlertCircle;
        const isCollapsed = collapsed[idx];

        return (
          <div
            key={idx}
            className="rounded-xl ring-1 ring-neutral-200 bg-white overflow-hidden"
          >
            {/* Header card */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-neutral-50 border-b border-neutral-100">
              <GripVertical className="w-4 h-4 text-neutral-300 flex-shrink-0" />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white ring-1 ring-neutral-200">
                  <Icon className="w-3.5 h-3.5 text-[#d03484]" />
                </span>
                <span className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                  #{idx + 1}
                </span>
                <span className="text-sm font-semibold text-[#30475e] truncate">
                  {meta?.label ?? <span className="text-amber-600">Type inconnu : {block.type}</span>}
                </span>
              </div>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  title="Monter"
                  className="p-1.5 rounded text-neutral-400 hover:text-[#30475e] hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === blocks.length - 1}
                  title="Descendre"
                  className="p-1.5 rounded text-neutral-400 hover:text-[#30475e] hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((prev) => ({ ...prev, [idx]: !prev[idx] }))
                  }
                  title={isCollapsed ? 'Déplier' : 'Replier'}
                  className="p-1.5 rounded text-neutral-400 hover:text-[#30475e] hover:bg-white transition-colors"
                >
                  {isCollapsed ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronUp className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  title="Supprimer ce bloc"
                  className="p-1.5 rounded text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Body card (form) */}
            {!isCollapsed && (
              <div className="p-4 space-y-3">
                <BlockForm
                  type={type}
                  block={block}
                  onUpdate={(patch) => update(idx, patch)}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Add block — galerie de modules */}
      {!addOpen && (
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-300 hover:border-[#d03484]/40 hover:bg-[#fbfaf8] px-4 py-3 text-sm font-semibold text-neutral-500 hover:text-[#d03484] transition-all"
        >
          <Plus className="w-4 h-4" />
          Ajouter un module
        </button>
      )}

      {addOpen && (
        <div className="rounded-2xl ring-1 ring-neutral-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-200">
            <div>
              <p className="text-sm font-bold text-[#30475e]">Choisissez un module</p>
              <p className="text-[11px] text-neutral-500">
                Cliquez pour ajouter à votre étape
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-[#30475e] hover:bg-white transition-colors"
              title="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(TYPE_META).map(([type, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => add(type)}
                  className="group flex items-start gap-3 p-3 rounded-xl ring-1 ring-neutral-200 hover:ring-[#d03484]/40 hover:bg-[#fbfaf8] hover:-translate-y-0.5 transition-all text-left"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#fde7f2] to-[#fde2d3] group-hover:from-[#d03484] group-hover:to-[#d47254] flex items-center justify-center transition-colors">
                    <Icon className="w-4 h-4 text-[#d03484] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-bold text-[#30475e]">
                        {meta.label}
                      </span>
                      {meta.advanced && (
                        <span className="text-[9px] font-bold text-neutral-400 uppercase tracking-widest bg-neutral-100 rounded-full px-1.5 py-0.5">
                          Avancé
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-neutral-500 leading-snug">
                      {meta.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Routeur de form par type ────────────────────────────────────────────────

function BlockForm({
  type,
  block,
  onUpdate,
}: {
  type: string;
  block: AnyBlock;
  onUpdate: (patch: Partial<AnyBlock>) => void;
}) {
  switch (type) {
    case 'rich-text':
      return <RichTextForm block={block} onUpdate={onUpdate} />;
    case 'image':
      return <ImageForm block={block} onUpdate={onUpdate} />;
    case 'stats-grid':
      return <StatsGridForm block={block} onUpdate={onUpdate} />;
    case 'timeline':
      return <TimelineForm block={block} onUpdate={onUpdate} />;
    case 'embed-iframe':
      return <EmbedIframeForm block={block} onUpdate={onUpdate} />;
    case 'video':
      return <VideoForm block={block} onUpdate={onUpdate} />;
    case 'pdf':
      return <PdfForm block={block} onUpdate={onUpdate} />;
    case 'table':
    case 'team-org':
      return <AdvancedJsonForm block={block} onUpdate={onUpdate} />;
    case 'a-vous-de-jouer':
      return (
        <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2.5 text-xs text-emerald-800 space-y-1">
          <p className="font-semibold">✅ Rien à configurer.</p>
          <p>
            Ce module affiche le formulaire "rencontrer l'équipe" : coordonnées
            pré-remplies via le lien d'invitation ATS, choix oui/non, motivation
            ou feedback. À la soumission : email au client (si oui) + copie
            recruteur, via l'ATS.
          </p>
          <p className="text-emerald-600">
            Réservé aux parcours découverte (pas aux onboardings) — pense à le
            placer dans la dernière étape du parcours.
          </p>
        </div>
      );
    default:
      return (
        <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-xs text-amber-700">
          Type de bloc <code>{type}</code> non reconnu — édition en JSON avancé.
        </div>
      );
  }
}

// ── Forms par type ──────────────────────────────────────────────────────────

function RichTextForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const html = (block.html as string) ?? '';

  function wrap(tagOpen: string, tagClose: string) {
    const ta = document.activeElement as HTMLTextAreaElement | null;
    if (!ta || ta.tagName !== 'TEXTAREA') return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const value = ta.value;
    const next = value.slice(0, start) + tagOpen + value.slice(start, end) + tagClose + value.slice(end);
    onUpdate({ html: next });
    // Refocus + place cursor inside the wrap
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + tagOpen.length, end + tagOpen.length);
    }, 0);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        <span className={`${labelClass} mb-0 mr-1`}>HTML</span>
        <button type="button" onClick={() => wrap('<strong>', '</strong>')} className="px-2 py-1 text-xs rounded ring-1 ring-neutral-200 hover:bg-neutral-50 font-bold">B</button>
        <button type="button" onClick={() => wrap('<em>', '</em>')} className="px-2 py-1 text-xs rounded ring-1 ring-neutral-200 hover:bg-neutral-50 italic">I</button>
        <button type="button" onClick={() => wrap('<p>', '</p>')} className="px-2 py-1 text-xs rounded ring-1 ring-neutral-200 hover:bg-neutral-50">¶</button>
        <button type="button" onClick={() => wrap('<h3>', '</h3>')} className="px-2 py-1 text-xs rounded ring-1 ring-neutral-200 hover:bg-neutral-50">H3</button>
        <button type="button" onClick={() => wrap('<ul>\n  <li>', '</li>\n</ul>')} className="px-2 py-1 text-xs rounded ring-1 ring-neutral-200 hover:bg-neutral-50">• Liste</button>
        <button type="button" onClick={() => wrap('<a href="https://">', '</a>')} className="px-2 py-1 text-xs rounded ring-1 ring-neutral-200 hover:bg-neutral-50 underline">Lien</button>
      </div>
      <textarea
        value={html}
        onChange={(e) => onUpdate({ html: e.target.value })}
        rows={6}
        className={`${inputClass} font-mono text-xs`}
        placeholder="<p>Votre contenu ici…</p>"
      />
      <p className="text-[11px] text-neutral-400">
        Sélectionnez du texte puis cliquez sur un bouton pour l'entourer.
      </p>
    </div>
  );
}

function ImageForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const url = (block.url as string) ?? '';
  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>URL de l'image</label>
        <input
          value={url}
          onChange={(e) => onUpdate({ url: e.target.value })}
          placeholder="https://…/image.png"
          className={inputClass}
        />
        {url && (
          <div className="mt-2 rounded-lg ring-1 ring-neutral-200 bg-neutral-50 p-2">
            <img src={url} alt="" className="max-h-32 max-w-full mx-auto rounded" />
          </div>
        )}
      </div>
      <div>
        <label className={labelClass}>Texte alternatif (accessibilité, SEO)</label>
        <input
          value={(block.alt as string) ?? ''}
          onChange={(e) => onUpdate({ alt: e.target.value })}
          placeholder="Description courte de l'image"
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Légende (optionnel, affichée sous l'image)</label>
        <input
          value={(block.caption as string) ?? ''}
          onChange={(e) => onUpdate({ caption: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Alignement</label>
        <select
          value={(block.align as string) ?? 'center'}
          onChange={(e) => onUpdate({ align: e.target.value })}
          className={inputClass}
        >
          <option value="left">Gauche</option>
          <option value="center">Centré</option>
          <option value="right">Droite</option>
        </select>
      </div>
    </div>
  );
}

function StatsGridForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const items = (block.items as Array<Record<string, string>>) ?? [];

  function updateItem(idx: number, key: string, value: string) {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    onUpdate({ items: next });
  }
  function addItem() {
    onUpdate({ items: [...items, { value: '', label: '', description: '' }] });
  }
  function removeItem(idx: number) {
    onUpdate({ items: items.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-neutral-500">
        {items.length} stat{items.length !== 1 ? 's' : ''} · {items.length === 4 ? 'parfait pour la grille 4 cols' : 'idéalement 4 items pour un rendu équilibré'}
      </p>

      {items.map((item, idx) => (
        <div key={idx} className="rounded-lg ring-1 ring-neutral-200 bg-neutral-50 p-3 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
              Stat {idx + 1}
            </span>
            <button
              type="button"
              onClick={() => removeItem(idx)}
              className="text-neutral-400 hover:text-red-600 p-1 rounded transition-colors"
              title="Supprimer cette stat"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">
                Valeur (chiffre)
              </label>
              <input
                value={item.value ?? ''}
                onChange={(e) => updateItem(idx, 'value', e.target.value)}
                placeholder="10k, 60, 200…"
                className={`${inputClass} bg-white`}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">
                Label
              </label>
              <input
                value={item.label ?? ''}
                onChange={(e) => updateItem(idx, 'label', e.target.value)}
                placeholder="Clients, Collaborateurs…"
                className={`${inputClass} bg-white`}
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">
              Description (optionnel — petite ligne sous la stat)
            </label>
            <input
              value={item.description ?? item.note ?? ''}
              onChange={(e) => updateItem(idx, 'description', e.target.value)}
              placeholder="Sur l'ensemble du territoire"
              className={`${inputClass} bg-white`}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addItem}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-neutral-300 hover:border-[#d03484]/40 hover:bg-[#fbfaf8] px-3 py-2 text-xs font-semibold text-neutral-500 hover:text-[#d03484] transition-all"
      >
        <Plus className="w-3.5 h-3.5" />
        Ajouter une stat
      </button>
    </div>
  );
}

function TimelineForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const items = (block.items as Array<Record<string, string>>) ?? [];

  function updateItem(idx: number, key: string, value: string) {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    onUpdate({ items: next });
  }
  function addItem() {
    onUpdate({ items: [...items, { year: '', headline: '', description: '' }] });
  }
  function removeItem(idx: number) {
    onUpdate({ items: items.filter((_, i) => i !== idx) });
  }
  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onUpdate({ items: next });
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-neutral-500">
        {items.length} événement{items.length !== 1 ? 's' : ''} dans la timeline
      </p>

      {items.map((item, idx) => (
        <div key={idx} className="rounded-lg ring-1 ring-neutral-200 bg-neutral-50 p-3 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
              Événement {idx + 1}
            </span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded text-neutral-400 hover:text-[#30475e] disabled:opacity-30 transition-colors" title="Monter">
                <ChevronUp className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1 rounded text-neutral-400 hover:text-[#30475e] disabled:opacity-30 transition-colors" title="Descendre">
                <ChevronDown className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => removeItem(idx)} className="p-1 rounded text-neutral-400 hover:text-red-600 transition-colors" title="Supprimer">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-[100px_1fr] gap-2">
            <div>
              <label className="block text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">Année</label>
              <input
                value={item.year ?? ''}
                onChange={(e) => updateItem(idx, 'year', e.target.value)}
                placeholder="1963"
                className={`${inputClass} bg-white`}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">Titre court</label>
              <input
                value={item.headline ?? ''}
                onChange={(e) => updateItem(idx, 'headline', e.target.value)}
                placeholder="Roger Ferrière rachète la société"
                className={`${inputClass} bg-white`}
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">Description</label>
            <textarea
              value={item.description ?? ''}
              onChange={(e) => updateItem(idx, 'description', e.target.value)}
              rows={2}
              placeholder="La société passe de 4 à 7 commerciaux et accélère sa croissance."
              className={`${inputClass} bg-white`}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addItem}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-neutral-300 hover:border-[#d03484]/40 hover:bg-[#fbfaf8] px-3 py-2 text-xs font-semibold text-neutral-500 hover:text-[#d03484] transition-all"
      >
        <Plus className="w-3.5 h-3.5" />
        Ajouter un événement
      </button>
    </div>
  );
}

function EmbedIframeForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const kind = ((block.kind as string) ?? 'other') as
    | 'form'
    | 'presentation'
    | 'calendar'
    | 'map'
    | 'other';
  const aspectRatio = (block.aspectRatio as string) ?? '';
  // Pour les présentations, on suggère un aspect-ratio plutôt qu'une hauteur fixe
  const useAspect = kind === 'presentation' || !!aspectRatio;

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>URL à intégrer</label>
        <input
          value={(block.src as string) ?? ''}
          onChange={(e) => onUpdate({ src: e.target.value })}
          placeholder="https://prezi.com/p/… · https://view.genial.ly/… · https://share.hsforms.com/…"
          className={inputClass}
        />
        <p className="text-[10px] text-neutral-400 mt-1">
          Prezi, Genially, Canva, Calendly, Google Maps, HubSpot, Tally, Typeform…
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className={labelClass}>Type de contenu</label>
          <select
            value={kind}
            onChange={(e) => onUpdate({ kind: e.target.value })}
            className={inputClass}
          >
            <option value="other">Auto-détecter depuis l'URL</option>
            <option value="presentation">📊 Présentation (Prezi, Genially, Canva)</option>
            <option value="form">📝 Formulaire (HubSpot, Tally, Typeform)</option>
            <option value="calendar">📅 Calendrier (Calendly, Cal.com)</option>
            <option value="map">📍 Carte (Google Maps, OpenStreetMap)</option>
          </select>
          <p className="text-[10px] text-neutral-400 mt-1">
            Change l'icône, le label et le bouton de chargement.
          </p>
        </div>
        <div>
          <label className={labelClass}>Titre (accessibilité)</label>
          <input
            value={(block.title as string) ?? ''}
            onChange={(e) => onUpdate({ title: e.target.value })}
            placeholder="Notre culture en 5 minutes"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className={labelClass}>Format</label>
          <select
            value={useAspect ? aspectRatio || '16/9' : 'fixed'}
            onChange={(e) => {
              if (e.target.value === 'fixed') {
                onUpdate({ aspectRatio: undefined });
              } else {
                onUpdate({ aspectRatio: e.target.value });
              }
            }}
            className={inputClass}
          >
            <option value="fixed">Hauteur fixe (px)</option>
            <option value="16/9">Ratio 16:9 (présentations widescreen)</option>
            <option value="4/3">Ratio 4:3 (présentations classiques)</option>
            <option value="1/1">Ratio 1:1 (carré)</option>
          </select>
        </div>
        {!useAspect && (
          <div>
            <label className={labelClass}>Hauteur (px)</label>
            <input
              type="number"
              value={(block.height as number) ?? 600}
              onChange={(e) => onUpdate({ height: parseInt(e.target.value, 10) || 600 })}
              min={200}
              max={2000}
              className={inputClass}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── VIDEO FORM ──────────────────────────────────────────────────────────────

function VideoForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const url = (block.url as string) ?? '';
  // Détection YouTube/Vimeo pour proposer un thumbnail par défaut
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  const autoThumb = ytMatch ? `https://i.ytimg.com/vi/${ytMatch[1]}/maxresdefault.jpg` : '';

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>URL de la vidéo (YouTube, Vimeo, Loom…)</label>
        <input
          value={url}
          onChange={(e) => onUpdate({ url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=… · https://vimeo.com/…"
          className={inputClass}
        />
        {ytMatch && (
          <p className="text-[10px] text-emerald-600 mt-1 font-medium">
            ✓ YouTube détecté · thumbnail auto disponible
          </p>
        )}
      </div>
      <div>
        <label className={labelClass}>Titre (affiché sur l'overlay play)</label>
        <input
          value={(block.title as string) ?? ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Notre vision du recrutement"
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>
          Miniature personnalisée{' '}
          <span className="font-normal text-neutral-400">
            (optionnel — auto-détectée pour YouTube)
          </span>
        </label>
        <input
          value={(block.thumbnail as string) ?? ''}
          onChange={(e) => onUpdate({ thumbnail: e.target.value })}
          placeholder={autoThumb || 'https://…/thumbnail.jpg'}
          className={inputClass}
        />
      </div>
    </div>
  );
}

// ── PDF FORM ────────────────────────────────────────────────────────────────

function PdfForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>URL du PDF</label>
        <input
          value={(block.url as string) ?? ''}
          onChange={(e) => onUpdate({ url: e.target.value })}
          placeholder="https://…/document.pdf"
          className={inputClass}
        />
        <p className="text-[10px] text-neutral-400 mt-1">
          Idéalement hébergé sur Supabase Storage (bucket public) pour éviter les blocages CORS.
        </p>
      </div>
      <div>
        <label className={labelClass}>Titre affiché</label>
        <input
          value={(block.title as string) ?? ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Plaquette de présentation 2026"
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Hauteur de la liseuse</label>
        <select
          value={(block.height as string) ?? '70vh'}
          onChange={(e) => onUpdate({ height: e.target.value })}
          className={inputClass}
        >
          <option value="50vh">Compact (50% écran)</option>
          <option value="70vh">Standard (70% écran)</option>
          <option value="90vh">Plein écran (90%)</option>
          <option value="600">Fixe — 600 px</option>
          <option value="800">Fixe — 800 px</option>
        </select>
      </div>
    </div>
  );
}

// Fallback : édition JSON brute pour les types peu utilisés (video, pdf, table, team-org)
function AdvancedJsonForm({ block, onUpdate }: { block: AnyBlock; onUpdate: (p: Partial<AnyBlock>) => void }) {
  const [draft, setDraft] = useState(() => JSON.stringify(block, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);

  function commit() {
    try {
      const parsed = JSON.parse(draft);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        setParseError('Le bloc doit être un objet JSON.');
        return;
      }
      setParseError(null);
      onUpdate(parsed);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-xs text-amber-700">
        Ce type de bloc (<code>{block.type as string}</code>) n'a pas encore d'éditeur visuel.
        Modifiez le JSON ci-dessous, puis cliquez sur « Appliquer ».
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={10}
        className={`${inputClass} font-mono text-xs`}
        spellCheck={false}
      />
      {parseError && (
        <p className="text-xs text-red-600">JSON invalide : {parseError}</p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={commit}
          className="rounded-lg bg-[#30475e] hover:bg-[#243648] text-white text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          Appliquer
        </button>
      </div>
    </div>
  );
}
